import { useQuery } from '@tanstack/react-query'
import type { HistoryPayload, MarketQuote, QuotesPayload, Range } from '@shared/markets/types.ts'

export type { HistoryPayload, MarketQuote, Range }

const MARKETS_URL = ((import.meta.env.VITE_MARKETS_URL as string | undefined)?.trim() || '/api/markets').replace(/\/$/, '')
const CACHE_KEY = 'gx-markets-cache'

function isQuotesPayload(x: unknown): x is QuotesPayload {
  const p = x as QuotesPayload | null
  return (
    !!p &&
    Array.isArray(p.quotes) &&
    p.quotes.every(
      (q) =>
        typeof q.id === 'string' &&
        Array.isArray(q.ranges) &&
        (q.status !== 'ok' ||
          (typeof q.price === 'number' && !!q.source?.name && typeof q.asOf === 'string' && !!q.kind)),
    )
  )
}

function readCache(): QuotesPayload | undefined {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : undefined
    return isQuotesPayload(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

async function getJson(path: string): Promise<unknown> {
  const res = await fetch(`${MARKETS_URL}${path}`, { headers: { accept: 'application/json' } })
  if (!res.ok) throw new Error(`markets HTTP ${res.status}`)
  return res.json()
}

export interface QuotesState {
  quotes: MarketQuote[] | null
  status: 'loading' | 'ready' | 'error'
  fromDeviceCache: boolean
  isFetching: boolean
  refetch: () => void
  byId: (id: string) => MarketQuote | undefined
}

/** Latest market quotes (server-cached), with the last good set kept on the device. */
export function useMarketQuotes(): QuotesState {
  const q = useQuery({
    queryKey: ['markets', 'quotes'],
    queryFn: async () => {
      const body = await getJson('/quotes')
      if (!isQuotesPayload(body)) throw new Error('markets: invalid payload')
      try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(body))
      } catch {
        /* storage unavailable */
      }
      return body
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
    initialData: readCache,
    initialDataUpdatedAt: 0,
  })
  const quotes = q.data?.quotes ?? null
  return {
    quotes,
    status: quotes ? 'ready' : q.isError ? 'error' : 'loading',
    fromDeviceCache: !!quotes && q.dataUpdatedAt === 0,
    isFetching: q.isFetching,
    refetch: () => void q.refetch(),
    byId: (id) => quotes?.find((x) => x.id === id),
  }
}

/** Chart history for one asset and range. */
export function useMarketHistory(id: string, range: Range | null) {
  return useQuery({
    queryKey: ['markets', 'history', id, range],
    enabled: !!range,
    queryFn: async () => {
      const body = (await getJson(
        `/history?asset=${encodeURIComponent(id)}&range=${encodeURIComponent(range!)}`,
      )) as HistoryPayload
      if (!Array.isArray(body?.points)) throw new Error('markets: invalid history')
      return body
    },
    staleTime: range === '1D' || range === '1W' ? 5 * 60 * 1000 : 60 * 60 * 1000,
    retry: 1,
  })
}
